import { useNavigate } from 'react-router-dom';
import { GSProduct } from '@/hooks/useGSProducts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, Calendar, Package } from 'lucide-react';

interface ProductCardProps {
  product: GSProduct;
}

export const GSProductCard = ({ product }: ProductCardProps) => {
  const navigate = useNavigate();
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'certified':
        return 'bg-green-100 text-green-800 hover:bg-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 hover:bg-red-200';
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'formulated':
        return <Package className="h-4 w-4" />;
      case 'building':
        return <Building2 className="h-4 w-4" />;
      default:
        return <Package className="h-4 w-4" />;
    }
  };

  const handleClick = () => {
    navigate(`/gs-client-portal/product/${product.id}`);
  };

  return (
    <Card 
      className="hover:shadow-md transition-shadow cursor-pointer"
      onClick={handleClick}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <CardTitle className="text-lg line-clamp-1">{product.name}</CardTitle>
          <Badge className={getStatusColor(product.certification_status)}>
            {product.certification_status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          {product.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {product.description}
            </p>
          )}
          
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              {getTypeIcon(product.product_type)}
              <span className="capitalize">{product.product_type}</span>
            </div>
            
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>{new Date(product.created_at).toLocaleDateString()}</span>
            </div>
          </div>

          {product.manufacturer && (
            <div className="text-xs text-muted-foreground">
              <span className="font-medium">Manufacturer:</span> {product.manufacturer}
            </div>
          )}

          <div className="text-xs text-muted-foreground">
            <span className="font-medium">Portfolio:</span> {product.portfolio.title}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};